// src/pages/CareersPage.jsx

import React, { useState, useEffect } from 'react';
import NavBarHome from '../Layouts/NavBarHome';
import FooterHome from '../Layouts/FooterHome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHexagonCheck, faFileUser } from '@fortawesome/pro-regular-svg-icons';

const styles = {
  container: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  introSection: {
    padding: '150px 20px',
    backgroundColor: '#ff2300',
    textAlign: 'center',
  },
  introTitle: {
    fontSize: '60px',
    color: '#fff',
    marginBottom: '20px',
    fontWeight: '700',
    lineHeight: '1.1',
  },
  introText: {
    fontSize: '18px',
    fontWeight: '400',
    color: '#fff',
    lineHeight: '1.6',
    maxWidth: '800px',
    margin: '0 auto',
  },
  mainSection: {
    padding: '50px 20px',
    backgroundColor: '#f9f9f9',
    flex: '1',
  },
  title: {
    fontSize: '48px',
    color: '#333',
    marginBottom: '40px',
    fontWeight: '700',
    textAlign: 'center',
  },
  section: {
    maxWidth: '900px',
    margin: '0 auto 60px auto',
    textAlign: 'center',
  },
  sectionText: {
    fontSize: '18px',
    color: '#333',
    lineHeight: '1.7',
    marginBottom: '20px',
    fontWeight: '500',
    whiteSpace: 'pre-wrap',
  },
  coreHeader: {
    textAlign: 'center',
    margin: '60px 0 40px',
  },
  coreTitle: {
    fontSize: '60px',
    margin: 0,
    fontWeight: '700',
    lineHeight: '1.1',
  },
  coreRed: {
    color: '#ff2300',
  },
  valueOrder: {
    fontSize: '16px',
    color: '#ff2300',
    fontWeight: '600',
    marginBottom: '4px',
  },
  valueTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111',
    margin: '0 0 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  valueSub: {
    fontStyle: 'italic',
    fontSize: '14px',
    fontWeight: '400',
    color: '#666',
    marginBottom: '12px',
  },
  button: {
    marginTop: '20px',
    padding: '12px 28px',
    fontSize: '16px',
    fontWeight: '500',
    backgroundColor: '#ff2300',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  specialSection: {
    textAlign: 'center',
    padding: '80px 20px',
    backgroundColor: '#fff',
  },
  logo: {
    display: 'block',
    margin: '0 auto 20px',
    width: '120px',
  },
};

const content = {
  es: {
    sections: [
      {
        text: 'Estamos convencidos de que el talento humano florece cuando se combina una estructura sólida, la libertad para explorar nuevas ideas y una confianza mutua que impulsa la innovación. Por eso fomentamos un entorno de trabajo en el que nuestro propio equipo practica estos valores cada día, aprendiendo, colaborando y superándose juntos.',
      },
      {
        values: [
          {
            title: 'Aprendizaje constante',
            sub: 'Despierta la curiosidad, impulsa el crecimiento y abre posibilidades infinitas.',
            text: 'Cada equipo prospera con el aprendizaje compartido. Fomentamos la curiosidad y el crecimiento entre los miembros para desbloquear el potencial ilimitado del equipo.',
          },
          {
            title: 'Colaboración real',
            sub: 'Fusiona perspectivas diversas en una acción colectiva apasionada y con propósito.',
            text: 'Cada miembro del equipo aporta ideas únicas. Por eso nuestros equipos aprovechan diversas perspectivas a través de un propósito compartido.',
          },
          {
            title: 'Confianza radical',
            sub: 'Asumir rápido los errores impulsa el crecimiento, la mejora y la innovación audaz.',
            text: 'Cada equipo evoluciona mediante la experimentación sin miedo. Por eso nuestros equipos asumen rápidamente los errores para refinar estrategias y fortalecer la resiliencia.',
          },
        ],
      },
    ],
  },
  en: {
    sections: [
      {
        text: 'We’re convinced that human talent flourishes when solid structure, the freedom to explore new ideas, and mutual trust that drives innovation come together. That’s why we foster a work environment where our team lives these values every day—learning, collaborating, and pushing themselves to excel.',
      },
      {
        values: [
          {
            title: 'Always learning',
            sub: 'Sparks curiosity, fuels growth, and unlocks endless possibilities.',
            text: 'Every team thrives on shared learning. We foster curiosity and growth among members to unlock boundless team potential.',
          },
          {
            title: 'Real collaboration',
            sub: 'Merges diverse perspectives into passionate, purposeful collective action.',
            text: 'Every team member brings unique insights. That’s why our teams harness diverse perspectives through shared purpose.',
          },
          {
            title: 'Radical trust',
            sub: 'Owning failures fast fuels growth, improvement, and bold innovation.',
            text: 'Every team evolves through fearless experimentation. That’s why our teams rapidly own mistakes to refine strategies and build resilience.',
          },
        ],
      },
    ],
  },
};

const coreLabels = {
  es: {
    title: 'Nuestro Mantra',
    order: ['Primero', 'Segundo', 'Tercero'],
  },
  en: {
    title: 'Our Mantra',
    order: ['First', 'Second', 'Third'],
  },
};

function CareersPage() {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('language') || 'es'
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const lang = content[language];
  const { title: cvTitle, order: cvOrder } = coreLabels[language];

  // Ajuste tamaño de "Lo que nos mueve"
  const moveTitleStyle = {
    ...styles.coreTitle,
    fontSize: isMobile ? '32px' : styles.coreTitle.fontSize,
  };

  // Ajuste tamaño de "Nuestro Mantra"
  const mantraTitleStyle = {
    ...styles.coreTitle,
    fontSize: isMobile ? '32px' : styles.coreTitle.fontSize,
  };

  return (
    <div style={styles.container}>
      <NavBarHome language={language} />

      {/* Introductorio */}
      <section style={styles.introSection}>
        <h1 style={styles.introTitle}>
          {language === 'es'
            ? 'Únete a algo que realmente importa.'
            : 'Join something that matters.'}
        </h1>
        <p style={styles.introText}>
          {language === 'es'
            ? 'No solo buscamos talento. Buscamos personas motivadas por un propósito — dispuestas a ayudar a construir un futuro mejor con inteligencia, empatía y visión.'
            : 'We’re not just looking for talent. We’re looking for purpose-driven people — ready to help shape a better future with intelligence, empathy and vision.'}
        </p>
      </section>

      {/* Contenido principal */}
      <main style={styles.mainSection}>
        {lang.sections.map((section, idx) => (
          <section
            key={idx}
            style={{
              ...styles.section,
              ...(idx === 0 ? { marginBottom: '150px' } : {}),
            }}
          >
            {idx === 0 && (
              <>
                <hr
                  style={{
                    border: 'none',
                    height: '2px',
                    backgroundColor: '#ff2300',
                    margin: '60px 0',
                  }}
                />
                <div style={styles.coreHeader}>
                  <h2 style={moveTitleStyle}>
                    <span style={{ color: '#222', marginRight: '8px' }}>
                      {language === 'es' ? 'Lo que nos' : 'What drives'}
                    </span>
                    <span style={styles.coreRed}>
                      {language === 'es' ? 'mueve' : 'us'}
                    </span>
                  </h2>
                </div>
                <p style={styles.sectionText}>{section.text}</p>
                <hr
                  style={{
                    border: 'none',
                    height: '2px',
                    backgroundColor: '#ff2300',
                    margin: '60px 0',
                  }}
                />
              </>
            )}

            {section.values && (
              <>
                <div style={styles.coreHeader}>
                  <h2 style={mantraTitleStyle}>
                    <span style={styles.coreRed}>{cvTitle.split(' ')[0]}</span>{' '}
                    {cvTitle.split(' ').slice(1).join(' ')}
                  </h2>
                </div>

                {isMobile ? (
                  section.values.map((val, i) => (
                    <div
                      key={i}
                      style={{
                        textAlign: 'center',
                        margin: '0 auto 40px',
                      }}
                    >
                      <p style={styles.valueOrder}>{cvOrder[i]}</p>
                      <h3
                        style={{
                          ...styles.valueTitle,
                          justifyContent: 'center',
                          margin: '0 auto 8px',
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faHexagonCheck}
                          style={{ marginRight: '8px', color: '#ff2300' }}
                        />
                        {val.title}
                      </h3>
                      <p style={styles.valueSub}>{val.sub}</p>
                      <p style={styles.sectionText}>{val.text}</p>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      rowGap: '10px',
                      columnGap: '60px',
                      textAlign: 'left',
                    }}
                  >
                    {section.values.map((val, i) => (
                      <h3 key={`t-${i}`} style={styles.valueTitle}>
                        <FontAwesomeIcon
                          icon={faHexagonCheck}
                          style={{ marginRight: '8px', color: '#ff2300' }}
                        />
                        {val.title}
                      </h3>
                    ))}
                    {section.values.map((val, i) => (
                      <p key={`s-${i}`} style={styles.valueSub}>
                        {val.sub}
                      </p>
                    ))}
                    {section.values.map((val, i) => (
                      <p key={`b-${i}`} style={styles.sectionText}>
                        {val.text}
                      </p>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        ))}
      </main>

      {/* Sección final */}
      <section style={styles.specialSection}>
        <img
          src="/BetherLogoLuminousOrange.png"
          alt="Bether logo"
          style={styles.logo}
        />
        <h2 style={{ ...styles.title, marginBottom: '2px' }}>
          {language === 'es'
            ? 'Construyendo equipos que prosperan'
            : 'Building teams that thrive'}
        </h2>
        <p style={styles.sectionText}>
          {language === 'es'
            ? 'Crecimiento empoderado. Colaboración diversa. Innovación sin miedo.'
            : 'Empowered growth. Diverse collaboration. Fearless innovation.'}
        </p>
        <a
          href="mailto:jobs@bether.ai"
          style={styles.button}
          role="button"
          aria-label={
            language === 'es'
              ? 'Enviar correo a jobs@bether.ai'
              : 'Send email to jobs@bether.ai'
          }
        >
          <FontAwesomeIcon
            icon={faFileUser}
            style={{ marginRight: '8px', color: '#fff' }}
          />
          {language === 'es' ? 'jobs@bether.ai' : 'jobs@bether.ai'}
        </a>
      </section>

      <FooterHome language={language} setLanguage={setLanguage} />
    </div>
  );
}

export default CareersPage;
